import { AfterViewInit, Component, OnDestroy, input, signal } from '@angular/core';
import { SpinnerDirective } from '../../../../ui-components/atoms/spinner/spinner.directive';

@Component({
  selector: 'dmcom-speech-to-text-listening',
  standalone: true,
  imports: [SpinnerDirective],
  template: `
    @if (!isLoading()) {
      <div class="flex items-center gap-4 h-24 w-full">
        @for (_ of bars; track $index; let i = $index) {
          <div class="bar rounded shrink-0 bg-muted size-4 min-h-4" [style.height.px]="heights()[i]"></div>
        }
      </div>
    } @else {
      <div dmcomSpinner color="primary"></div>
    }
  `,
})
export class SpeechToTextListeningComponent implements AfterViewInit, OnDestroy {
  private readonly BAR_COUNT = 81;
  private readonly MIN_H = 4; // px
  private readonly MAX_H = 48; // px
  private readonly SILENCE = 0.05; // gate threshold
  private readonly GAIN = 3; // amplitude multiplier
  private readonly ATTACK = 0.4; // rising‑edge smoothing
  private readonly RELEASE = 0.1; // falling‑edge smoothing
  private readonly SMOOTHING = 0.9; // WebAudio smoothing constant

  readonly isRecording = input<boolean>();
  readonly isLoading = input<boolean>();
  readonly bars = Array.from({ length: this.BAR_COUNT });
  readonly heights = signal<number[]>(Array.from({ length: this.BAR_COUNT }).map(() => this.MIN_H));

  private readonly amplitudes = new Array(this.BAR_COUNT);
  private audio?: {
    ctx: AudioContext;
    analyser: AnalyserNode;
    data: Uint8Array;
    raf: number;
    stream: MediaStream;
  };

  ngAfterViewInit(): void {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const ctx = new (window.AudioContext ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
          (window as any).webkitAudioContext)();

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256; // 128 samples
        analyser.smoothingTimeConstant = this.SMOOTHING;

        ctx.createMediaStreamSource(stream).connect(analyser);

        this.audio = {
          ctx,
          analyser,
          data: new Uint8Array(analyser.fftSize),
          raf: requestAnimationFrame(() => this.render()),
          stream,
        };
      })
      .catch((err: Error) => console.error('Failed to access microphone:', err.message || err));
  }

  private render(): void {
    if (!this.audio) return;

    const { analyser, data } = this.audio;
    analyser.getByteTimeDomainData(data);

    const stride = Math.floor(data.length / this.BAR_COUNT);
    this.amplitudes.fill(0); // Reset amplitudes array

    for (let i = 0; i < this.BAR_COUNT; i++) {
      const sample = data[i * stride];
      let amp = Math.abs(sample - 128) / 128; // 0–1

      amp = amp < this.SILENCE ? 0 : Math.min(1, amp * this.GAIN);
      this.amplitudes[i] = amp;
    }

    this.amplitudes.unshift(this.amplitudes.pop()); // shift ►

    const prev = this.heights();
    const next = this.amplitudes.map((amp, i) => {
      const target = this.MIN_H + amp * (this.MAX_H - this.MIN_H);
      const coefficient = target > prev[i] ? this.ATTACK : this.RELEASE;
      return prev[i] + (target - prev[i]) * coefficient;
    });

    this.heights.set(next);

    if (this.isRecording()) {
      this.audio.raf = requestAnimationFrame(() => this.render());
    }
  }

  private resetHeights(): void {
    this.heights.set(Array.from({ length: this.BAR_COUNT }).map(() => this.MIN_H));
  }

  ngOnDestroy(): void {
    if (this.audio) {
      cancelAnimationFrame(this.audio.raf);
      void this.audio.ctx.close();
      this.audio.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }
}
